import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import {
  Archive,
  ArchiveRestore,
  ChevronRight,
  Code2,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  History,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import clsx from 'clsx';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useTree } from '@/context/TreeContext';
import StatusBadge from '@/components/StatusBadge';
import {
  computeRecency,
  normalizeRecency,
  DEFAULT_RECENCY_DAYS,
  RECENCY_CHOICES,
  type RecencyDays,
} from '@/lib/recency';
import type { TreeNode } from '@/lib/types';

const STORAGE_KEY = 'str-wiki:sidebar-state';

const HTML_STARTER =
  '<!doctype html>\n<html>\n<body>\n  <h1>New HTML page</h1>\n</body>\n</html>';

function loadState(): {
  folders: string[];
  collapsedSections: string[];
  collapsedDocs: string[];
  openArchives: string[];
  recencyDays: RecencyDays;
} {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        folders: Array.isArray(parsed.folders) ? parsed.folders : [],
        collapsedSections: Array.isArray(parsed.collapsedSections) ? parsed.collapsedSections : [],
        collapsedDocs: Array.isArray(parsed.collapsedDocs) ? parsed.collapsedDocs : [],
        openArchives: Array.isArray(parsed.openArchives) ? parsed.openArchives : [],
        // `undefined` (never chosen) -> the default window; an explicit `null` -> All time.
        recencyDays: normalizeRecency(parsed.recencyDays),
      };
    }
  } catch {
    /* ignore corrupt state */
  }
  return {
    folders: [],
    collapsedSections: [],
    collapsedDocs: [],
    openArchives: [],
    recencyDays: DEFAULT_RECENCY_DAYS,
  };
}

/** The catch-all section for pages whose header was deleted — pinned last by its
 * position (9999, set server-side) and hidden when it has nothing to show. */
const NO_HEADER_ID = 'no-header';

/** Depth guard for pathological trees; also keeps indentation sane. */
const MAX_DEPTH = 32;
const NO_ANCESTORS: ReadonlySet<string> = new Set();

/** A drop lands either as a sibling of the hovered row, or as its last child. */
type DropZone = 'before' | 'after' | 'inside';
/** Drop targets are keyed like childrenByParent: a node id, or `root:<sectionId>`. */
interface DropTarget {
  key: string;
  zone: DropZone;
}
const rootKey = (sectionId: string) => `root:${sectionId}`;
const isRootKey = (key: string) => key.startsWith('root:');
const sectionOfRootKey = (key: string) => key.slice(5);

/** Fraction of a row's height that counts as the "reorder" edge rather than "nest inside". */
const EDGE_RATIO = 0.28;

export default function Sidebar() {
  const { tree, loading, error, refresh, patchNode, applyMove, applySectionOrder } = useTree();
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;

  const [creating, setCreating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Which "+" menu is open, keyed by `section:<id>` or `folder:<id>`.
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  // Right-click menu: null when closed.
  const [ctxMenu, setCtxMenu] = useState<CtxMenuState | null>(null);
  // Inline rename.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  // Drag-and-drop (nodes).
  const [dragId, setDragId] = useState<string | null>(null);
  const [drop, setDrop] = useState<DropTarget | null>(null);
  // Drag-and-drop (whole sections, admin re-order). Separate state: a section drag
  // and a node drag must never be confused for one another.
  const [dragSection, setDragSection] = useState<string | null>(null);
  const [sectionDrop, setSectionDrop] = useState<{ id: string; zone: 'before' | 'after' } | null>(
    null
  );
  // Modal flows: node pending deletion, section create/delete.
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [sectionModal, setSectionModal] = useState<
    { mode: 'create' } | { mode: 'delete'; sectionId: string } | null
  >(null);

  const initial = loadState();
  const [openFolders, setOpenFolders] = useState<Set<string>>(new Set(initial.folders));
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set(initial.collapsedSections)
  );
  // Doc pages that HAVE children are expanded by default (so nothing that was
  // visible in the flat list ever disappears); we persist only the collapsed ones.
  const [collapsedDocs, setCollapsedDocs] = useState<Set<string>>(new Set(initial.collapsedDocs));
  // Per-section "Archived" groups — closed by default, so we persist the OPEN ones.
  const [openArchives, setOpenArchives] = useState<Set<string>>(new Set(initial.openArchives));
  // "Past X days" window; null = All time. See lib/recency.ts for the rules.
  const [recencyDays, setRecencyDays] = useState<RecencyDays>(initial.recencyDays);

  const navigate = useNavigate();
  const match = useMatch('/doc/:id/*');
  const activeId = match?.params.id ?? null;

  // Persist expand/collapse state.
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        folders: [...openFolders],
        collapsedSections: [...collapsedSections],
        collapsedDocs: [...collapsedDocs],
        openArchives: [...openArchives],
        recencyDays,
      })
    );
  }, [openFolders, collapsedSections, collapsedDocs, openArchives, recencyDays]);

  // Index nodes by parent for O(1) child lookups, and record each node's RENDERED parent
  // key (which is not always its parentId — see below). Edge cases handled here:
  // - Orphans (parentId pointing at a node that isn't in the tree — deleted, or in
  //   a section this user can't see) fall back to the root of their OWN section
  //   rather than being silently dropped.
  // - A child whose parent lives in a DIFFERENT visible section renders under the
  //   parent, in the parent's section — the hierarchy wins over the child's sectionId
  //   (matches the breadcrumb trail in DocView).
  // - Nodes trapped in a parentId cycle (a→b→a) are unreachable from any root; the
  //   reachability sweep re-roots them in their own section so they still render.
  const { childrenByParent, parentKeyById, byId } = useMemo(() => {
    const map = new Map<string, TreeNode[]>();
    const parentKey = new Map<string, string>();
    const ids = new Map<string, TreeNode>();
    if (!tree) return { childrenByParent: map, parentKeyById: parentKey, byId: ids };
    for (const n of tree.nodes) ids.set(n.id, n);

    const push = (key: string, n: TreeNode) => {
      parentKey.set(n.id, key);
      const arr = map.get(key);
      if (arr) arr.push(n);
      else map.set(key, [n]);
    };

    for (const n of tree.nodes) {
      push(n.parentId && ids.has(n.parentId) ? n.parentId : rootKey(n.sectionId), n);
    }
    // Reachability sweep (cycle rescue).
    const reachable = new Set<string>();
    const stack = tree.sections.map((s) => rootKey(s.id));
    while (stack.length) {
      const key = stack.pop()!;
      for (const child of map.get(key) ?? []) {
        if (reachable.has(child.id)) continue;
        reachable.add(child.id);
        stack.push(child.id);
      }
    }
    for (const n of tree.nodes) {
      if (reachable.has(n.id)) continue;
      const from = parentKey.get(n.id);
      if (from) {
        const arr = map.get(from);
        const i = arr?.indexOf(n) ?? -1;
        if (arr && i >= 0) arr.splice(i, 1);
      }
      push(rootKey(n.sectionId), n);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.position - b.position);
    return { childrenByParent: map, parentKeyById: parentKey, byId: ids };
  }, [tree]);

  // Roots of the per-section "Archived" groups: archived nodes whose rendered parent is
  // NOT itself archived (those render nested under their archived ancestor). Bucketed by
  // the node's OWN sectionId — the archive group lives under its section header even when
  // the node's parent renders in another section.
  const archiveRootsBySection = useMemo(() => {
    const m = new Map<string, TreeNode[]>();
    if (!tree) return m;
    for (const n of tree.nodes) {
      if (!n.isArchived) continue;
      const pk = parentKeyById.get(n.id);
      const parent = pk && !isRootKey(pk) ? byId.get(pk) : undefined;
      if (parent?.isArchived) continue;
      const arr = m.get(n.sectionId);
      if (arr) arr.push(n);
      else m.set(n.sectionId, [n]);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.position - b.position);
    return m;
  }, [tree, parentKeyById, byId]);

  // "Past X days" filter. Pure logic (and its reasoning) lives in lib/recency.ts;
  // all this does is translate the sidebar's rendered-parent keys into plain parent
  // ids and pin the doc currently open. Recomputed only when the tree, the window
  // or the active page changes — the clock is read at that moment, not on a timer.
  const recency = useMemo(() => {
    const parentById = new Map<string, string | null>();
    for (const [id, key] of parentKeyById) parentById.set(id, isRootKey(key) ? null : key);
    return computeRecency(
      tree?.nodes ?? [],
      parentById,
      recencyDays,
      activeId ? [activeId] : []
    );
  }, [tree, parentKeyById, recencyDays, activeId]);

  /** Does this node survive the recency window? Always true when it's off. */
  const inWindow = useCallback(
    (id: string) => !recency.filtering || recency.visible.has(id),
    [recency]
  );

  // Auto-expand the ancestors (and section) of the active doc so it's visible.
  useEffect(() => {
    if (!activeId || !tree) return;
    const ancestorsToOpen: string[] = [];
    const seen = new Set<string>();
    let cur = byId.get(activeId);
    const sectionId = cur?.sectionId;
    // If the active doc renders inside an "Archived" group (it or an ancestor is
    // archived), that group has to open too or the doc stays hidden.
    let archived = cur?.isArchived ?? false;
    while (cur?.parentId && !seen.has(cur.id)) {
      seen.add(cur.id);
      ancestorsToOpen.push(cur.parentId);
      cur = byId.get(cur.parentId);
      if (cur?.isArchived) archived = true;
    }
    if (archived && sectionId) {
      setOpenArchives((prev) => (prev.has(sectionId) ? prev : new Set(prev).add(sectionId)));
    }
    if (ancestorsToOpen.length) {
      setOpenFolders((prev) => {
        const next = new Set(prev);
        ancestorsToOpen.forEach((id) => next.add(id));
        return next;
      });
      setCollapsedDocs((prev) => {
        const next = new Set(prev);
        let changed = false;
        for (const id of ancestorsToOpen) if (next.delete(id)) changed = true;
        return changed ? next : prev;
      });
    }
    if (sectionId) {
      setCollapsedSections((prev) => {
        if (!prev.has(sectionId)) return prev;
        const next = new Set(prev);
        next.delete(sectionId);
        return next;
      });
    }
  }, [activeId, tree, byId]);

  const toggleFolder = useCallback((id: string) => {
    setOpenFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleDoc = useCallback((id: string) => {
    setCollapsedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSection = useCallback((id: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleArchive = useCallback((sectionId: string) => {
    setOpenArchives((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  /** Expand a parent so a node that just moved/was created into it stays visible. */
  const revealParent = useCallback((parentId: string | null) => {
    if (!parentId) return;
    setOpenFolders((prev) => new Set(prev).add(parentId));
    setCollapsedDocs((prev) => {
      if (!prev.has(parentId)) return prev;
      const next = new Set(prev);
      next.delete(parentId);
      return next;
    });
  }, []);

  const startRename = useCallback(
    (id: string) => {
      setCtxMenu(null);
      setRenameValue(byId.get(id)?.title ?? '');
      setRenamingId(id);
    },
    [byId]
  );

  const commitRename = useCallback(async () => {
    const id = renamingId;
    if (!id) return;
    const title = renameValue.trim();
    setRenamingId(null);
    const cur = byId.get(id);
    if (!cur || !title || title === cur.title) return;
    patchNode(id, { title }); // sidebar (and breadcrumbs) update immediately
    try {
      await api.updateNode(id, { title });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Rename failed');
      await refresh(); // pull the server's title back, undoing the optimistic patch
    }
  }, [renamingId, renameValue, byId, patchNode, refresh]);

  // -------------------------------- create --------------------------------

  const handleNew = useCallback(
    async (sectionId: string, parentId: string | null, kind: 'tiptap' | 'html') => {
      if (creating) return;
      setOpenMenu(null);
      setCtxMenu(null);
      setCreating(true);
      try {
        const node = await api.createNode(
          kind === 'html'
            ? {
                sectionId,
                parentId,
                type: 'doc',
                title: 'Untitled',
                contentType: 'html',
                html: HTML_STARTER,
              }
            : {
                sectionId,
                parentId,
                type: 'doc',
                title: 'Untitled',
              }
        );
        revealParent(parentId);
        await refresh();
        navigate(`/doc/${node.id}/edit`);
      } catch (e) {
        setActionError(e instanceof Error ? e.message : 'Failed to create page');
      } finally {
        setCreating(false);
      }
    },
    [creating, refresh, revealParent, navigate]
  );

  const handleNewFolder = useCallback(
    async (sectionId: string, parentId: string | null) => {
      if (creating) return;
      setOpenMenu(null);
      setCtxMenu(null);
      setCreating(true);
      try {
        const node = await api.createNode({
          sectionId,
          parentId,
          type: 'folder',
          title: 'New folder',
        });
        revealParent(parentId);
        await refresh();
        // Folders have no view route — don't navigate. Drop straight into rename
        // instead, so "New folder" → type a name → Enter is one uninterrupted gesture.
        setRenameValue('New folder');
        setRenamingId(node.id);
      } catch (e) {
        setActionError(e instanceof Error ? e.message : 'Failed to create folder');
      } finally {
        setCreating(false);
      }
    },
    [creating, refresh, revealParent]
  );

  // --------------------------- archive / delete ---------------------------

  const subtreeOf = useCallback(
    (id: string): Set<string> => {
      const out = new Set<string>([id]);
      const stack = [id];
      while (stack.length) {
        for (const kid of childrenByParent.get(stack.pop()!) ?? []) {
          if (out.has(kid.id)) continue;
          out.add(kid.id);
          stack.push(kid.id);
        }
      }
      return out;
    },
    [childrenByParent]
  );

  const handleArchive = useCallback(
    async (id: string) => {
      setCtxMenu(null);
      const node = byId.get(id);
      try {
        await api.archiveNode(id);
        // Reveal where it went, so archiving never looks like a delete.
        if (node) setOpenArchives((prev) => new Set(prev).add(node.sectionId));
      } catch (e) {
        setActionError(e instanceof Error ? e.message : 'Archive failed');
      } finally {
        await refresh();
      }
    },
    [byId, refresh]
  );

  const handleRestore = useCallback(
    async (id: string) => {
      setCtxMenu(null);
      try {
        await api.restoreNode(id);
      } catch (e) {
        setActionError(e instanceof Error ? e.message : 'Restore failed');
      } finally {
        await refresh();
      }
    },
    [refresh]
  );

  const handleDelete = useCallback(
    async (id: string, children: 'delete' | 'reparent') => {
      setDeleteTargetId(null);
      const gone = children === 'delete' ? subtreeOf(id) : new Set([id]);
      try {
        await api.trashNode(id, children);
        if (activeId && gone.has(activeId)) navigate('/');
      } catch (e) {
        setActionError(e instanceof Error ? e.message : 'Delete failed');
      } finally {
        await refresh();
      }
    },
    [subtreeOf, activeId, navigate, refresh]
  );

  const handleCreateSection = useCallback(
    async (name: string) => {
      setSectionModal(null);
      if (!name.trim()) return;
      try {
        await api.adminCreateSection(name.trim());
      } catch (e) {
        setActionError(e instanceof Error ? e.message : 'Failed to create section');
      } finally {
        await refresh();
      }
    },
    [refresh]
  );

  const handleDeleteSection = useCallback(
    async (sectionId: string) => {
      setSectionModal(null);
      // No navigation: the section's pages survive the delete (they move to
      // No Header, keeping their ids), so an open doc stays valid.
      try {
        await api.adminDeleteSection(sectionId);
      } catch (e) {
        setActionError(e instanceof Error ? e.message : 'Failed to delete section');
      } finally {
        await refresh();
      }
    },
    [refresh]
  );

  // ------------------------------ drag & drop ------------------------------

  const dragNode = dragId ? byId.get(dragId) ?? null : null;

  /** The dragged node plus everything under it — none of it is a legal destination. */
  const dragSubtree = useMemo(
    () => (dragId ? subtreeOf(dragId) : new Set<string>()),
    [dragId, subtreeOf]
  );

  const dropAllowed = useCallback(
    (key: string, zone: DropZone): boolean => {
      if (!dragNode) return false;
      // Cross-section drops are legal: the server relocates the node + its subtree to
      // the destination partition (create+delete, stable ids). The only hard rule left:
      // never drop a node onto itself or into its own subtree (it would detach).
      if (isRootKey(key)) return true;
      const target = byId.get(key);
      if (!target) return false;
      if (dragSubtree.has(target.id)) return false;
      if (zone !== 'inside' && !parentKeyById.get(target.id)) return false;
      return true;
    },
    [dragNode, byId, dragSubtree, parentKeyById]
  );

  /** Turn a hovered row + zone into the exact payload the move API wants. The
   *  destination section is the new parent's section (or the root's own section) —
   *  hierarchy and sectionId must agree, that's the server's contract. */
  const resolveDrop = useCallback(
    (
      key: string,
      zone: DropZone
    ): { parentId: string | null; sectionId: string; order: string[] } | null => {
      if (!dragId || !dropAllowed(key, zone)) return null;
      const siblingIds = (parentK: string) =>
        (childrenByParent.get(parentK) ?? []).map((n) => n.id).filter((x) => x !== dragId);

      // Dropped on a section header (or its "Empty" placeholder) → append to that root.
      if (isRootKey(key)) {
        return { parentId: null, sectionId: sectionOfRootKey(key), order: [...siblingIds(key), dragId] };
      }
      // Dropped on the body of a row → become its last child.
      if (zone === 'inside') {
        return {
          parentId: key,
          sectionId: byId.get(key)!.sectionId,
          order: [...siblingIds(key), dragId],
        };
      }
      // Dropped on a row's top/bottom edge → become its sibling, above or below it.
      const parentK = parentKeyById.get(key) ?? rootKey(byId.get(key)!.sectionId);
      const sibs = siblingIds(parentK);
      const idx = sibs.indexOf(key);
      const at = idx < 0 ? sibs.length : zone === 'before' ? idx : idx + 1;
      return {
        parentId: isRootKey(parentK) ? null : parentK,
        sectionId: isRootKey(parentK)
          ? sectionOfRootKey(parentK)
          : byId.get(parentK)!.sectionId,
        order: [...sibs.slice(0, at), dragId, ...sibs.slice(at)],
      };
    },
    [dragId, dropAllowed, childrenByParent, parentKeyById, byId]
  );

  const handleDrop = useCallback(
    async (key: string, zone: DropZone) => {
      const id = dragId;
      const move = id ? resolveDrop(key, zone) : null;
      setDragId(null);
      setDrop(null);
      if (!id || !move) return;

      // Lands under the cursor, no round-trip. Descendants' sectionIds are stale until
      // refresh(), but they render under their parent regardless, so nothing jumps.
      applyMove(id, move.parentId, move.sectionId, move.order);
      revealParent(move.parentId);
      try {
        await api.moveNode(id, move);
      } catch (e) {
        setActionError(e instanceof Error ? e.message : 'Move failed');
      } finally {
        // Reconcile with the server — and on failure, this is what reverts the optimism.
        await refresh();
      }
    },
    [dragId, resolveDrop, applyMove, revealParent, refresh]
  );

  /** Drag props for a page/folder row: it is both a drag source and a drop target. */
  const rowDnd = (node: TreeNode) => ({
    draggable: renamingId !== node.id,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', node.id);
      setDragId(node.id);
      setCtxMenu(null);
    },
    onDragEnd: () => {
      setDragId(null);
      setDrop(null);
    },
    onDragOver: (e: React.DragEvent) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const edge = Math.max(4, rect.height * EDGE_RATIO);
      const y = e.clientY - rect.top;
      const zone: DropZone = y < edge ? 'before' : y > rect.height - edge ? 'after' : 'inside';
      if (!dragId || !dropAllowed(node.id, zone)) {
        // No preventDefault ⇒ the browser shows "not allowed" and fires no drop here.
        // Also drop the indicator, or the last legal row keeps showing one under a
        // cursor that is now parked somewhere it cannot land.
        setDrop(null);
        return;
      }
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDrop((d) => (d?.key === node.id && d.zone === zone ? d : { key: node.id, zone }));
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (drop?.key === node.id) void handleDrop(drop.key, drop.zone);
    },
  });

  /** Drop props for a section header / its empty placeholder: append to the section root. */
  const rootDnd = (sectionId: string) => {
    const key = rootKey(sectionId);
    return {
      onDragOver: (e: React.DragEvent) => {
        if (!dragId || !dropAllowed(key, 'inside')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDrop((d) => (d?.key === key ? d : { key, zone: 'inside' }));
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (drop?.key === key) void handleDrop(key, 'inside');
      },
    };
  };

  // -------------------------- section re-ordering --------------------------

  const handleSectionReorder = useCallback(
    async (targetId: string, zone: 'before' | 'after') => {
      const id = dragSection;
      setDragSection(null);
      setSectionDrop(null);
      if (!id || !tree || id === targetId) return;

      const rest = [...tree.sections]
        .sort((a, b) => a.position - b.position)
        .map((s) => s.id)
        .filter((sid) => sid !== NO_HEADER_ID && sid !== id);
      const idx = rest.indexOf(targetId);
      if (idx < 0) return;
      const at = zone === 'before' ? idx : idx + 1;
      const order = [...rest.slice(0, at), id, ...rest.slice(at)];

      applySectionOrder(order); // lands under the cursor, no round-trip
      try {
        await api.adminReorderSections(order);
      } catch (e) {
        setActionError(e instanceof Error ? e.message : 'Re-order failed');
      } finally {
        await refresh(); // reconcile — and on failure, revert the optimism
      }
    },
    [dragSection, tree, applySectionOrder, refresh]
  );

  /**
   * Combined drag props for a section HEADER. It plays three roles:
   *  - drop target for node drags (append to this section's root) — delegated to rootDnd;
   *  - drag source for re-ordering the section itself (admins; No Header stays pinned);
   *  - drop target for another section being dragged (top half = before, bottom = after).
   */
  const headerDnd = (sectionId: string) => {
    const root = rootDnd(sectionId);
    const reorderable = isAdmin && sectionId !== NO_HEADER_ID;
    return {
      draggable: reorderable,
      onDragStart: (e: React.DragEvent) => {
        if (!reorderable) return;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', `section:${sectionId}`);
        setDragSection(sectionId);
        setCtxMenu(null);
        setOpenMenu(null);
      },
      onDragEnd: () => {
        setDragSection(null);
        setSectionDrop(null);
      },
      onDragOver: (e: React.DragEvent) => {
        if (!dragSection) return root.onDragOver(e);
        if (dragSection === sectionId || sectionId === NO_HEADER_ID) {
          setSectionDrop(null);
          return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const rect = e.currentTarget.getBoundingClientRect();
        const zone: 'before' | 'after' = e.clientY - rect.top < rect.height / 2 ? 'before' : 'after';
        setSectionDrop((d) => (d?.id === sectionId && d.zone === zone ? d : { id: sectionId, zone }));
      },
      onDrop: (e: React.DragEvent) => {
        if (!dragSection) return root.onDrop(e);
        e.preventDefault();
        e.stopPropagation();
        if (sectionDrop?.id === sectionId) void handleSectionReorder(sectionId, sectionDrop.zone);
      },
    };
  };

  // -------------------------------- render --------------------------------

  const renderNode = (
    node: TreeNode,
    depth: number,
    ancestors: ReadonlySet<string>,
    inArchive = false
  ): JSX.Element | null => {
    // Cycle/depth guard: never render a node inside its own subtree.
    if (ancestors.has(node.id) || depth > MAX_DEPTH) return null;
    // Archived nodes render only in their section's "Archived" group (with their whole
    // subtree, archived or not); the main tree skips them.
    if (!inArchive && node.isArchived) return null;
    // Recency window. The "Archived" group is exempt — it's the deliberate old-stuff
    // bucket and is collapsed by default. Filtering `kids` (not just returning null
    // from the child's own render) keeps `hasKids` honest, so a folder whose contents
    // are all out of window doesn't offer a chevron onto nothing.
    if (!inArchive && !inWindow(node.id)) return null;
    const indent = { paddingLeft: `${Math.min(depth, 12) * 12 + 8}px` };
    const allKids = childrenByParent.get(node.id) ?? [];
    const kids = inArchive ? allKids : allKids.filter((k) => !k.isArchived && inWindow(k.id));
    const childAncestors = kids.length > 0 ? new Set([...ancestors, node.id]) : ancestors;
    const isRenaming = renamingId === node.id;
    const hit = drop?.key === node.id ? drop.zone : null;

    // The row is both drag source and drop target; the indicator is drawn inside it.
    // Archive rows opt out of drag-and-drop entirely.
    const rowClass = clsx(
      'group relative flex items-center gap-1 rounded pr-1 text-sm',
      dragId === node.id && 'opacity-40',
      inArchive && 'opacity-70',
      hit === 'inside' && 'bg-accent/10 ring-1 ring-accent'
    );
    const indicator = hit === 'before' || hit === 'after' ? (
      <span
        aria-hidden
        className={clsx(
          'pointer-events-none absolute left-1 right-1 h-0.5 rounded bg-accent',
          hit === 'before' ? 'top-0' : 'bottom-0'
        )}
      />
    ) : null;

    const onContextMenu = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setOpenMenu(null);
      setCtxMenu({
        x: e.clientX,
        y: e.clientY,
        sectionId: node.sectionId,
        parentId: node.id,
        nodeId: node.id,
        inArchive,
      });
    };

    if (node.type === 'folder') {
      const isOpen = openFolders.has(node.id);
      return (
        <div key={node.id}>
          <div
            className={clsx(rowClass, 'text-ink-muted hover:bg-surface-2')}
            style={indent}
            onContextMenu={onContextMenu}
            {...(inArchive ? {} : rowDnd(node))}
          >
            {indicator}
            <button
              type="button"
              onClick={() => toggleFolder(node.id)}
              className="flex w-3.5 shrink-0 items-center justify-center py-1 text-ink-faint hover:text-ink"
            >
              <ChevronRight
                size={14}
                className={clsx('shrink-0 transition-transform', isOpen && 'rotate-90')}
              />
            </button>
            {isOpen ? (
              <FolderOpen size={15} className="shrink-0 text-ink-faint" />
            ) : (
              <Folder size={15} className="shrink-0 text-ink-faint" />
            )}
            {isRenaming ? (
              <RenameInput
                value={renameValue}
                onChange={setRenameValue}
                onCommit={() => void commitRename()}
                onCancel={() => setRenamingId(null)}
              />
            ) : (
              <button
                type="button"
                onClick={() => toggleFolder(node.id)}
                onDoubleClick={() => startRename(node.id)}
                className="min-w-0 flex-1 truncate py-1 text-left"
              >
                {node.title}
              </button>
            )}
            {!inArchive && (
              <NewMenu
                title="New page in folder"
                iconSize={13}
                hoverBg="hover:bg-surface-3"
                open={openMenu === `folder:${node.id}`}
                disabled={creating}
                onToggle={() =>
                  setOpenMenu((m) => (m === `folder:${node.id}` ? null : `folder:${node.id}`))
                }
                onClose={() => setOpenMenu(null)}
                onCreate={(kind) => handleNew(node.sectionId, node.id, kind)}
                onCreateFolder={() => handleNewFolder(node.sectionId, node.id)}
              />
            )}
          </div>
          {isOpen && kids.length > 0 && (
            <div>{kids.map((k) => renderNode(k, depth + 1, childAncestors, inArchive))}</div>
          )}
        </div>
      );
    }

    // doc — may itself be a parent (pages published with parentId nest under it).
    const isActive = node.id === activeId;
    const hasKids = kids.length > 0;
    const isOpen = hasKids && !collapsedDocs.has(node.id);
    return (
      <div key={node.id}>
        <div
          style={indent}
          className={clsx(
            rowClass,
            'py-1',
            isActive && hit !== 'inside'
              ? 'bg-accent/10 font-medium text-accent'
              : 'text-ink-muted hover:bg-surface-2'
          )}
          onContextMenu={onContextMenu}
          {...(inArchive ? {} : rowDnd(node))}
        >
          {indicator}
          {hasKids ? (
            <button
              type="button"
              onClick={() => toggleDoc(node.id)}
              title={isOpen ? 'Collapse' : 'Expand'}
              className="flex w-3.5 shrink-0 items-center justify-center text-ink-faint hover:text-ink"
            >
              <ChevronRight
                size={14}
                className={clsx('shrink-0 transition-transform', isOpen && 'rotate-90')}
              />
            </button>
          ) : (
            /* Spacer to align with folders' chevron */
            <span className="w-3.5 shrink-0" />
          )}
          {isRenaming ? (
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              <FileText size={15} className="shrink-0 text-ink-faint" />
              <RenameInput
                value={renameValue}
                onChange={setRenameValue}
                onCommit={() => void commitRename()}
                onCancel={() => setRenamingId(null)}
              />
            </div>
          ) : (
            <Link
              to={`/doc/${node.id}`}
              /* The row owns the drag. A native <a> drag would hijack it with a URL payload. */
              draggable={false}
              className="flex min-w-0 flex-1 items-center gap-1.5"
            >
              <FileText
                size={15}
                className={clsx('shrink-0', isActive ? 'text-accent' : 'text-ink-faint')}
              />
              <span className="truncate">{node.title || 'Untitled'}</span>
              <StatusBadge status={node.status} />
            </Link>
          )}
        </div>
        {isOpen && (
          <div>{kids.map((k) => renderNode(k, depth + 1, childAncestors, inArchive))}</div>
        )}
      </div>
    );
  };

  return (
    <nav
      className="flex h-full flex-col overflow-y-auto px-2 py-3 text-sm"
      onDragLeave={(e) => {
        // Only clear when the pointer actually leaves the nav, not on child-to-child moves.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setDrop(null);
          setSectionDrop(null);
        }
      }}
    >
      {loading && <div className="px-2 py-1 text-ink-faint">Loading…</div>}
      {error && (
        <div className="px-2 py-1 text-xs text-red-500">
          {error}{' '}
          <button type="button" onClick={() => void refresh()} className="underline">
            Retry
          </button>
        </div>
      )}
      {actionError && (
        <div className="px-2 py-1 text-xs text-red-500">
          {actionError}{' '}
          <button type="button" onClick={() => setActionError(null)} className="underline">
            Dismiss
          </button>
        </div>
      )}

      {tree && (
        <RecencyFilter
          days={recencyDays}
          hiddenCount={recency.hiddenCount}
          onChange={setRecencyDays}
        />
      )}

      {tree &&
        [...tree.sections]
          .sort((a, b) => a.position - b.position)
          .map((section) => {
            const isOpen = !collapsedSections.has(section.id);
            const allRoots = childrenByParent.get(rootKey(section.id)) ?? [];
            const roots = allRoots.filter((n) => !n.isArchived && inWindow(n.id));
            const hiddenHere = recency.hiddenBySection.get(section.id) ?? 0;
            const archRoots = archiveRootsBySection.get(section.id) ?? [];
            const archOpen = openArchives.has(section.id);
            // The catch-all only appears once something actually lives in it.
            if (section.id === NO_HEADER_ID && allRoots.length === 0 && archRoots.length === 0) {
              return null;
            }
            const rootHit = drop?.key === rootKey(section.id);
            const secHit = sectionDrop?.id === section.id ? sectionDrop.zone : null;
            return (
              <div key={section.id} className="mb-2">
                <div
                  className={clsx(
                    'group relative flex items-center gap-1 rounded',
                    dragSection === section.id && 'opacity-40',
                    rootHit && 'bg-accent/10 ring-1 ring-accent'
                  )}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setOpenMenu(null);
                    setCtxMenu({
                      x: e.clientX,
                      y: e.clientY,
                      sectionId: section.id,
                      parentId: null,
                      nodeId: null,
                      inArchive: false,
                    });
                  }}
                  {...headerDnd(section.id)}
                >
                  {secHit && (
                    <span
                      aria-hidden
                      className={clsx(
                        'pointer-events-none absolute left-0 right-0 h-0.5 rounded bg-accent',
                        secHit === 'before' ? 'top-0' : 'bottom-0'
                      )}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    className="flex flex-1 items-center gap-1 py-1 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint hover:text-ink-muted"
                  >
                    <ChevronRight
                      size={13}
                      className={clsx('shrink-0 transition-transform', isOpen && 'rotate-90')}
                    />
                    <span className="truncate">{section.name}</span>
                  </button>
                  <NewMenu
                    title="New page in section"
                    iconSize={14}
                    hoverBg="hover:bg-surface-2"
                    open={openMenu === `section:${section.id}`}
                    disabled={creating}
                    onToggle={() =>
                      setOpenMenu((m) =>
                        m === `section:${section.id}` ? null : `section:${section.id}`
                      )
                    }
                    onClose={() => setOpenMenu(null)}
                    onCreate={(kind) => handleNew(section.id, null, kind)}
                    onCreateFolder={() => handleNewFolder(section.id, null)}
                  />
                </div>
                {isOpen && (
                  <div className="mt-0.5">
                    {roots.length > 0 ? (
                      roots.map((n) => renderNode(n, 1, NO_ANCESTORS))
                    ) : (
                      /* Still a drop target when it's the filter that emptied it. */
                      <div
                        className={clsx(
                          'rounded px-2 py-1 pl-6 text-xs text-ink-faint',
                          rootHit && 'bg-accent/10'
                        )}
                        {...rootDnd(section.id)}
                      >
                        {hiddenHere > 0 ? `${hiddenHere} older hidden` : 'Empty'}
                      </div>
                    )}
                    {roots.length > 0 && hiddenHere > 0 && (
                      <div className="px-2 py-0.5 pl-6 text-[11px] text-ink-faint">
                        +{hiddenHere} older
                      </div>
                    )}
                    {archRoots.length > 0 && (
                      <div className="mt-1">
                        <button
                          type="button"
                          onClick={() => toggleArchive(section.id)}
                          className="flex w-full items-center gap-1 rounded px-2 py-1 text-left text-xs text-ink-faint hover:bg-surface-2 hover:text-ink-muted"
                        >
                          <ChevronRight
                            size={12}
                            className={clsx('shrink-0 transition-transform', archOpen && 'rotate-90')}
                          />
                          <Archive size={12} className="shrink-0" />
                          <span>Archived</span>
                          <span className="text-ink-faint">({archRoots.length})</span>
                        </button>
                        {archOpen && (
                          <div>{archRoots.map((n) => renderNode(n, 1, NO_ANCESTORS, true))}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

      {tree && isAdmin && (
        <button
          type="button"
          disabled={creating}
          onClick={() => setSectionModal({ mode: 'create' })}
          className="mt-1 flex items-center gap-1.5 rounded px-2 py-1 text-left text-xs text-ink-faint hover:bg-surface-2 hover:text-ink-muted disabled:opacity-50"
        >
          <Plus size={13} className="shrink-0" />
          New section
        </button>
      )}

      {tree && tree.sections.length === 0 && !loading && (
        <div className="px-2 py-1 text-xs text-ink-faint">No sections yet.</div>
      )}

      {ctxMenu && (
        <ContextMenu
          state={ctxMenu}
          disabled={creating}
          onClose={() => setCtxMenu(null)}
          onCreate={
            ctxMenu.inArchive ? undefined : (kind) => handleNew(ctxMenu.sectionId, ctxMenu.parentId, kind)
          }
          onCreateFolder={
            ctxMenu.inArchive ? undefined : () => handleNewFolder(ctxMenu.sectionId, ctxMenu.parentId)
          }
          onRename={ctxMenu.nodeId ? () => startRename(ctxMenu.nodeId!) : undefined}
          onArchive={
            ctxMenu.nodeId && !byId.get(ctxMenu.nodeId)?.isArchived
              ? () => void handleArchive(ctxMenu.nodeId!)
              : undefined
          }
          onRestore={
            ctxMenu.nodeId && byId.get(ctxMenu.nodeId)?.isArchived
              ? () => void handleRestore(ctxMenu.nodeId!)
              : undefined
          }
          onDelete={
            ctxMenu.nodeId
              ? () => {
                  setCtxMenu(null);
                  setDeleteTargetId(ctxMenu.nodeId);
                }
              : undefined
          }
          onNewSection={
            !ctxMenu.nodeId && isAdmin
              ? () => {
                  setCtxMenu(null);
                  setSectionModal({ mode: 'create' });
                }
              : undefined
          }
          onDeleteSection={
            !ctxMenu.nodeId && isAdmin && ctxMenu.sectionId !== NO_HEADER_ID
              ? () => {
                  setCtxMenu(null);
                  setSectionModal({ mode: 'delete', sectionId: ctxMenu.sectionId });
                }
              : undefined
          }
        />
      )}

      {deleteTargetId && byId.get(deleteTargetId) && (
        <DeleteNodeModal
          node={byId.get(deleteTargetId)!}
          descendantCount={subtreeOf(deleteTargetId).size - 1}
          onCancel={() => setDeleteTargetId(null)}
          onDelete={(children) => void handleDelete(deleteTargetId, children)}
        />
      )}

      {sectionModal?.mode === 'create' && (
        <NewSectionModal
          onCancel={() => setSectionModal(null)}
          onCreate={(name) => void handleCreateSection(name)}
        />
      )}

      {sectionModal?.mode === 'delete' && tree && (
        <DeleteSectionModal
          name={tree.sections.find((s) => s.id === sectionModal.sectionId)?.name ?? 'section'}
          pageCount={tree.nodes.filter((n) => n.sectionId === sectionModal.sectionId).length}
          onCancel={() => setSectionModal(null)}
          onDelete={() => void handleDeleteSection(sectionModal.sectionId)}
        />
      )}
    </nav>
  );
}

/* --------------------------------------------------------------------------
   "Past X days" control. Always states the current window; when the window is
   actually hiding something it says how much and offers the one-click way out.
   Nothing here is ever the only route to a page — search, links and "All time"
   all still reach it.
   -------------------------------------------------------------------------- */

function RecencyFilter({
  days,
  hiddenCount,
  onChange,
}: {
  days: RecencyDays;
  hiddenCount: number;
  onChange: (d: RecencyDays) => void;
}) {
  return (
    <div className="mb-2 flex items-center gap-1 px-1 text-xs text-ink-faint">
      <History size={13} className="shrink-0" />
      <select
        value={days == null ? 'all' : String(days)}
        onChange={(e) => onChange(e.target.value === 'all' ? null : Number(e.target.value))}
        title="Hide sidebar items not touched within…"
        aria-label="Show pages updated within"
        className="min-w-0 flex-1 cursor-pointer truncate rounded border border-transparent bg-transparent py-0.5 text-xs text-ink-muted outline-none hover:border-border"
      >
        {RECENCY_CHOICES.map((d) => (
          <option key={d} value={d}>
            Last {d} days
          </option>
        ))}
        <option value="all">All time</option>
      </select>
      {days != null && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => onChange(null)}
          title={`${hiddenCount} older item${hiddenCount === 1 ? '' : 's'} hidden — show everything`}
          className="shrink-0 rounded px-1 py-0.5 hover:bg-surface-2 hover:text-ink-muted"
        >
          {hiddenCount} hidden
        </button>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
   Inline rename field (tree-level rename; the editor's title input still works).
   -------------------------------------------------------------------------- */

interface RenameInputProps {
  value: string;
  onChange: (v: string) => void;
  onCommit: () => void;
  onCancel: () => void;
}

function RenameInput({ value, onChange, onCommit, onCancel }: RenameInputProps) {
  return (
    <input
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={onCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur(); // onBlur commits
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
      onClick={(e) => e.stopPropagation()}
      className="min-w-0 flex-1 rounded border px-1 py-0.5 text-sm text-ink outline-none"
      style={{ borderColor: 'var(--accent)', background: 'var(--surface)' }}
    />
  );
}

/* --------------------------------------------------------------------------
   Right-click menu. Same actions as the "+" hover menu, plus Rename.
   -------------------------------------------------------------------------- */

interface CtxMenuState {
  x: number;
  y: number;
  sectionId: string;
  /** Where a new child would go: a node id, or null for the section root. */
  parentId: string | null;
  /** The right-clicked node, or null when the section header was clicked. */
  nodeId: string | null;
  /** True when the row lives in a section's "Archived" group (no create/dnd there). */
  inArchive: boolean;
}

interface ContextMenuProps {
  state: CtxMenuState;
  disabled: boolean;
  onClose: () => void;
  onCreate?: (kind: 'tiptap' | 'html') => void;
  onCreateFolder?: () => void;
  onRename?: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
  onDelete?: () => void;
  onNewSection?: () => void;
  onDeleteSection?: () => void;
}

const CTX_MENU_W = 176;
const CTX_ITEM_H = 30;

function ContextMenu({
  state,
  disabled,
  onClose,
  onCreate,
  onCreateFolder,
  onRename,
  onArchive,
  onRestore,
  onDelete,
  onNewSection,
  onDeleteSection,
}: ContextMenuProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const itemCount =
    (onCreate ? 2 : 0) +
    (onCreateFolder ? 1 : 0) +
    (onRename ? 1 : 0) +
    (onArchive || onRestore ? 1 : 0) +
    (onDelete ? 1 : 0) +
    (onNewSection ? 1 : 0) +
    (onDeleteSection ? 1 : 0);

  // Keep the menu on-screen when right-clicking near an edge.
  const left = Math.min(state.x, window.innerWidth - CTX_MENU_W - 8);
  const top = Math.min(state.y, window.innerHeight - (itemCount * CTX_ITEM_H + 30) - 8);

  const divider = <div className="my-1 h-px" style={{ background: 'var(--border)' }} />;
  const hasCreate = !!(onCreate || onCreateFolder);
  const hasNodeActions = !!(onRename || onArchive || onRestore || onDelete);
  const hasSectionActions = !!(onNewSection || onDeleteSection);

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} aria-hidden />
      <div
        role="menu"
        className="fixed z-50 overflow-hidden rounded-md border border-border bg-surface py-1 shadow-lg"
        style={{ left, top, width: CTX_MENU_W }}
      >
        {onCreate && (
          <>
            <MenuItem icon={<FileText size={14} />} disabled={disabled} onClick={() => onCreate('tiptap')}>
              New page
            </MenuItem>
            <MenuItem icon={<Code2 size={14} />} disabled={disabled} onClick={() => onCreate('html')}>
              New HTML page
            </MenuItem>
          </>
        )}
        {onCreateFolder && (
          <MenuItem icon={<FolderPlus size={14} />} disabled={disabled} onClick={onCreateFolder}>
            New folder
          </MenuItem>
        )}
        {hasCreate && hasNodeActions && divider}
        {onRename && (
          <MenuItem icon={<Pencil size={14} />} disabled={false} onClick={onRename}>
            Rename
          </MenuItem>
        )}
        {onArchive && (
          <MenuItem icon={<Archive size={14} />} disabled={disabled} onClick={onArchive}>
            Archive
          </MenuItem>
        )}
        {onRestore && (
          <MenuItem icon={<ArchiveRestore size={14} />} disabled={disabled} onClick={onRestore}>
            Unarchive
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem icon={<Trash2 size={14} />} disabled={disabled} onClick={onDelete} danger>
            Delete…
          </MenuItem>
        )}
        {(hasCreate || hasNodeActions) && hasSectionActions && divider}
        {onNewSection && (
          <MenuItem icon={<Plus size={14} />} disabled={disabled} onClick={onNewSection}>
            New section…
          </MenuItem>
        )}
        {onDeleteSection && (
          <MenuItem icon={<Trash2 size={14} />} disabled={disabled} onClick={onDeleteSection} danger>
            Delete section…
          </MenuItem>
        )}
      </div>
    </>
  );
}

function MenuItem({
  icon,
  disabled,
  onClick,
  danger = false,
  children,
}: {
  icon: JSX.Element;
  disabled: boolean;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm disabled:opacity-50',
        danger
          ? 'text-red-500 hover:bg-red-500/10'
          : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
      )}
    >
      <span className={clsx('shrink-0', danger ? 'text-red-500' : 'text-ink-faint')}>{icon}</span>
      {children}
    </button>
  );
}

/* --------------------------------------------------------------------------
   "+" affordance: a tiny menu to create a rich-text page, an HTML page, or a folder.
   -------------------------------------------------------------------------- */

interface NewMenuProps {
  title: string;
  iconSize: number;
  hoverBg: string;
  open: boolean;
  disabled: boolean;
  onToggle: () => void;
  onClose: () => void;
  onCreate: (kind: 'tiptap' | 'html') => void;
  onCreateFolder: () => void;
}

function NewMenu({
  title,
  iconSize,
  hoverBg,
  open,
  disabled,
  onToggle,
  onClose,
  onCreate,
  onCreateFolder,
}: NewMenuProps) {
  return (
    <span className="relative">
      <button
        type="button"
        title={title}
        disabled={disabled}
        onClick={onToggle}
        className={clsx(
          'rounded p-1 text-ink-faint hover:text-ink group-hover:opacity-100 disabled:opacity-50',
          hoverBg,
          open ? 'opacity-100' : 'opacity-0'
        )}
      >
        <Plus size={iconSize} />
      </button>
      {open && (
        <>
          {/* Click-away backdrop */}
          <div className="fixed inset-0 z-20" onClick={onClose} aria-hidden />
          <div className="absolute right-0 z-30 mt-1 w-40 overflow-hidden rounded-md border border-border bg-surface py-1 shadow-lg">
            <MenuItem icon={<FileText size={14} />} disabled={false} onClick={() => onCreate('tiptap')}>
              New page
            </MenuItem>
            <MenuItem icon={<Code2 size={14} />} disabled={false} onClick={() => onCreate('html')}>
              New HTML page
            </MenuItem>
            <MenuItem icon={<FolderPlus size={14} />} disabled={false} onClick={onCreateFolder}>
              New folder
            </MenuItem>
          </div>
        </>
      )}
    </span>
  );
}

/* --------------------------------------------------------------------------
   Modal dialogs: delete-a-node (with the children decision), new section,
   delete section. One shared shell.
   -------------------------------------------------------------------------- */

function ModalShell({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-sm rounded-lg border border-border bg-surface p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

const modalBtn =
  'rounded-md px-3 py-1.5 text-sm font-medium border border-border text-ink-muted hover:bg-surface-2';
const modalBtnDanger =
  'rounded-md px-3 py-1.5 text-sm font-medium bg-red-600 text-white hover:bg-red-500';
const modalBtnPrimary =
  'rounded-md px-3 py-1.5 text-sm font-medium text-white hover:opacity-90';

function DeleteNodeModal({
  node,
  descendantCount,
  onCancel,
  onDelete,
}: {
  node: TreeNode;
  descendantCount: number;
  onCancel: () => void;
  onDelete: (children: 'delete' | 'reparent') => void;
}) {
  const label = node.type === 'folder' ? 'folder' : 'page';
  return (
    <ModalShell onClose={onCancel}>
      <h2 className="text-sm font-semibold text-ink">
        Delete “{node.title || 'Untitled'}”?
      </h2>
      {descendantCount > 0 ? (
        <>
          <p className="mt-2 text-sm text-ink-muted">
            This {label} has {descendantCount} sub-item{descendantCount === 1 ? '' : 's'}. What
            should happen to them?
          </p>
          <div className="mt-4 flex flex-col gap-2">
            <button type="button" className={modalBtn} onClick={() => onDelete('reparent')}>
              Keep them — move up a level
            </button>
            <button type="button" className={modalBtnDanger} onClick={() => onDelete('delete')}>
              Delete everything ({descendantCount + 1} items)
            </button>
            <button type="button" className={modalBtn} onClick={onCancel}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-ink-muted">
            The {label} will be removed from the wiki.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className={modalBtn} onClick={onCancel}>
              Cancel
            </button>
            <button type="button" className={modalBtnDanger} onClick={() => onDelete('reparent')}>
              Delete
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

function NewSectionModal({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (name: string) => void;
}) {
  const [name, setName] = useState('');
  return (
    <ModalShell onClose={onCancel}>
      <h2 className="text-sm font-semibold text-ink">New section</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) onCreate(name);
        }}
      >
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Section name"
          className="mt-3 w-full rounded-md border px-2 py-1.5 text-sm text-ink outline-none"
          style={{ borderColor: 'var(--accent)', background: 'var(--surface)' }}
        />
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className={modalBtn} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim()}
            className={clsx(modalBtnPrimary, 'disabled:opacity-50')}
            style={{ background: 'var(--accent)' }}
          >
            Create
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function DeleteSectionModal({
  name,
  pageCount,
  onCancel,
  onDelete,
}: {
  name: string;
  pageCount: number;
  onCancel: () => void;
  onDelete: () => void;
}) {
  return (
    <ModalShell onClose={onCancel}>
      <h2 className="text-sm font-semibold text-ink">Delete section “{name}”?</h2>
      <p className="mt-2 text-sm text-ink-muted">
        {pageCount > 0 ? (
          <>
            Its {pageCount} item{pageCount === 1 ? '' : 's'} won’t be deleted — they move to a{' '}
            “No Header” section at the bottom of the sidebar.
          </>
        ) : (
          <>The section is empty; nothing else changes.</>
        )}
      </p>
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className={modalBtn} onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className={modalBtnDanger} onClick={onDelete}>
          Delete section
        </button>
      </div>
    </ModalShell>
  );
}
